// Client-safe shared types for the Store.

export type FulfillmentMethod = 'shipping'

export type StoreProductStatus = 'draft' | 'active' | 'archived'

export interface StoreImage {
  /** Storage path inside the private `store-products` bucket. */
  path: string
  /** Signed, time-limited display URL (populated server-side). */
  url?: string
  alt?: string | null
}

export interface StoreProduct {
  id: string
  slug: string
  name: string
  description: string | null
  category: string | null
  price_cents: number
  sale_price_cents: number | null
  inventory_qty: number | null
  in_stock: boolean
  allow_shipping: boolean
  status: StoreProductStatus
  featured: boolean
  sort_order: number
  images: StoreImage[]
}

export interface StoreSettingsPublic {
  store_enabled: boolean
  shipping_enabled: boolean
  shipping_flat_cents: number
  tax_enabled?: boolean
  allowed_shipping_countries: string[]
}

export function effectivePriceCents(p: {
  price_cents: number
  sale_price_cents: number | null
}): number {
  return p.sale_price_cents != null && p.sale_price_cents < p.price_cents
    ? p.sale_price_cents
    : p.price_cents
}

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export function isPurchasable(p: StoreProduct): boolean {
  return (
    p.status === 'active' &&
    p.in_stock &&
    (p.inventory_qty == null || p.inventory_qty > 0)
  )
}

/** Flat-rate shipping, waived once the subtotal reaches the threshold. */
export const FREE_SHIPPING_THRESHOLD_CENTS = 5000

export function shippingCentsFor(
  subtotalCents: number,
  flatRateCents: number,
): number {
  return subtotalCents >= FREE_SHIPPING_THRESHOLD_CENTS ? 0 : flatRateCents
}

/** Card & sticker bundle pricing: 1 = $30, 2 = $50, 5 = $100. */
export const STICKER_TIERS: Array<{ qty: number; cents: number }> = [
  { qty: 5, cents: 10000 },
  { qty: 2, cents: 5000 },
  { qty: 1, cents: 3000 },
]

export function isStickerProduct(p: {
  category?: string | null
  name?: string | null
  slug?: string | null
}): boolean {
  const hay = `${p.category ?? ''} ${p.name ?? ''} ${p.slug ?? ''}`.toLowerCase()
  return hay.includes('sticker')
}

/** Products (like Google review cards) programmed specifically for the customer's business. */
export function isGoogleReviewProduct(p: {
  category?: string | null
  name?: string | null
  slug?: string | null
}): boolean {
  const hay = `${p.category ?? ''} ${p.name ?? ''} ${p.slug ?? ''}`.toLowerCase()
  return hay.includes('google')
}

/** Products that require the pre-checkout customization acknowledgment. */
export function isCustomizedProduct(p: {
  category?: string | null
  name?: string | null
  slug?: string | null
}): boolean {
  return isStickerProduct(p) || isGoogleReviewProduct(p)
}

/** Best (cheapest) bundle combination for a sticker quantity. */
export function stickerLineTotalCents(quantity: number): number {
  let remaining = Math.max(0, Math.floor(quantity))
  let total = 0
  for (const tier of STICKER_TIERS) {
    const packs = Math.floor(remaining / tier.qty)
    total += packs * tier.cents
    remaining -= packs * tier.qty
  }
  // A partial remainder can be cheaper bought as the next bundle up.
  return Math.min(
    total,
    quantity > 0 ? Math.ceil(quantity / 5) * 10000 : 0,
  )
}

/** Line total honoring sticker bundles, otherwise unit price × quantity. */
export function lineTotalCents(
  p: { category?: string | null; name?: string | null; slug?: string | null },
  unitPriceCents: number,
  quantity: number,
): number {
  return isCustomizedProduct(p)
    ? stickerLineTotalCents(quantity)
    : unitPriceCents * quantity
}
